/**
 * Count-in (DEC-269, 2026-10-11): during the last bar before a running
 * timeline fires a canvas item, a ring fills around that item, so nothing the
 * timeline does surprises the performer and they can jump in first.
 *
 * A PREDICTION, and allowed to be one: "about to fire" is drawn from the
 * playhead, while "did fire" comes only from OPBridge (`canvasLights.ts`).
 *
 * Works on every device, including one that neither launched nor authored
 * the song, by joining four things that already exist:
 *   - which song is playing where, and exactly where: the timeline transport
 *     (`transport.ts`, 2026-10-16), following every paired bridge, because a
 *     song plays on its HOME bridge (DEC-224), often not the primary; it reads
 *     OPBridge's own playhead and that bridge's own clock;
 *   - the song itself: the synced timeline library (`getTimeline`);
 *   - where it goes next: `playheadAt`, the same section rules OPBridge plays.
 *
 * Known limits, all fine for a count-in: a start from elsewhere shows within
 * about a second (the transport's idle read); a launcher's own Loop override
 * is not in the snapshot (the timeline's default is used); this device's copy
 * of the song can trail an edit by the library sync's 15 s.
 */

import { createSignal, createRoot, createEffect, on, createMemo, untrack, type Accessor } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import { timelineLoops } from '@opbuttons/schema';
import { bridges, primaryBridge } from '../opbridge/store.ts';
import { activeProject } from '../layout/projectStore.ts';
import { followTransport, transportAwaitingStart, transportRun, transportBar, transportBeat, transportStartBeat } from './transport.ts';
import { getTimeline } from './store.ts';
import { playheadAt } from './playhead.ts';
import { laneAudible, canPlayFromTimeline, type FireableItem } from './bridgeSync.ts';

/** One fire on a canvas item that gets a count-in. */
export interface CueTarget { itemId: string; position: number }

const FIREABLE = new Set(['button', 'presetChange', 'snapshotChange', 'streamdeck']);

/** The fires on canvas items a song holds that get a count-in: `buttonRef`
 *  steps on audible lanes, on items a timeline can actually play, and only
 *  the FIRST of a run. A fire less than a bar after the previous fire on the
 *  same item would restart the ring before it filled (a hats lane in 8ths),
 *  so a run counts in once, at its start. PURE. */
export function countInTargets(t: Timeline, isPlayable: (itemId: string) => boolean): CueTarget[] {
  const byItem = new Map<string, number[]>();
  for (const lane of t.lanes) {
    if (!laneAudible(t, lane)) continue;
    for (const s of lane.steps) {
      if (s.kind !== 'buttonRef' || !isPlayable(s.buttonId)) continue;
      const list = byItem.get(s.buttonId) ?? [];
      list.push(s.position);
      byItem.set(s.buttonId, list);
    }
  }
  const out: CueTarget[] = [];
  for (const [itemId, positions] of byItem) {
    positions.sort((a, b) => a - b);
    let prev = -Infinity;
    for (const p of positions) {
      if (p - prev >= 1) out.push({ itemId, position: p });
      prev = p;
    }
  }
  return out;
}

/** Bars until each target item's next fire within `horizon` bars, walking
 *  the playhead forward from `anchorBar` + `elapsedBars` through the song's
 *  section rules. The walk is sampled every `horizon / samples` bars so a
 *  section loop or a whole-song wrap inside the horizon is handled. PURE. */
export function barsUntilFires(
  t: Timeline, targets: readonly CueTarget[], anchorBar: number, elapsedBars: number,
  loops: boolean, horizon = 1, samples = 32,
): Map<string, number> {
  const out = new Map<string, number>();
  const at0 = playheadAt(t, anchorBar, Math.max(0, elapsedBars), loops);
  if (at0.ended || at0.held || targets.length === 0) return out;
  const step = horizon / samples;
  let prev = at0.pos;
  for (let k = 1; k <= samples; k++) {
    const at = playheadAt(t, anchorBar, Math.max(0, elapsedBars) + k * step, loops);
    const cur = at.pos;
    const segStart = (k - 1) * step;
    for (const tg of targets) {
      let d: number | null = null;
      if (cur >= prev) {
        if (tg.position > prev && tg.position <= cur) d = segStart + (tg.position - prev);
      } else if (tg.position > prev && tg.position <= prev + step) {
        d = segStart + (tg.position - prev);               // just before a wrap
      } else if (tg.position <= cur && tg.position > cur - step) {
        d = segStart + step - (cur - tg.position);         // just after it
      }
      if (d !== null && d > 0) {
        const have = out.get(tg.itemId);
        if (have === undefined || d < have) out.set(tg.itemId, d);
      }
    }
    if (at.ended || at.held) break;
    prev = cur;
  }
  return out;
}

const [progress, setProgress] = createSignal<Readonly<Record<string, number>>>({});

/** How full this item's count-in ring is, 0..1, or undefined when nothing is
 *  coming for it within a bar. */
export function countInProgress(itemId: string): number | undefined {
  return progress()[itemId];
}

export interface CountInClock {
  beat: Accessor<number>;
  bpm: Accessor<number>;
  beatsPerBar: Accessor<number>;
}

let started = false;

/** Rings fill from OPBridge's own position (timeline/transport.ts, 2026-10-16), on every paired bridge: a song plays
 *  on its HOME bridge (DEC-224), often not the primary. Before, each ring was anchored on a 1 Hz state poll at the
 *  poll's time, so during a song's lead-in (OPBridge reports the parked start bar while queued) the anchor was set
 *  early and its half-bar tolerance could keep it early for the whole song (the transport audit). While a run is
 *  QUEUED, the rings count to where OPBridge starts it: the beat it named when it has (a song with a long intro starts
 *  on its bar 1, a later line, S323), else the next bar line of that bridge's clock. `clock` is kept for callers; the
 *  transport reads each bridge's own clock. */
export function startCountIn(_clock: CountInClock): void {
  if (started) return;
  started = true;
  const targetsCache = new WeakMap<Timeline, CueTarget[]>();
  let raf = 0;
  const followed = new Map<string, () => void>();

  const isPlayable = (itemId: string): boolean => {
    const proj = activeProject();
    if (!proj) return false;
    for (const pg of proj.pages) {
      const it = pg.items.find((i) => i.id === itemId);
      if (it) return FIREABLE.has(it.kind) && canPlayFromTimeline(it as FireableItem);
    }
    return false;
  };
  const targetsOf = (t: Timeline): CueTarget[] => {
    let v = targetsCache.get(t);
    if (!v) { v = countInTargets(t, isPlayable); targetsCache.set(t, v); }
    return v;
  };

  const anyRunning = (): boolean => [...followed.keys()].some((k) => !!transportRun(k));

  function frame(): void {
    raf = 0;
    const next: Record<string, number> = {};
    for (const target of followed.keys()) {
      const run = transportRun(target);
      if (!run || run.state === 'paused') continue;
      const t = getTimeline(run.timelineId);
      if (!t) continue;
      const cues = targetsOf(t);
      if (cues.length === 0) continue;
      if (run.state === 'queued') {
        // Starts on the beat OPBridge named, else the next bar line of its clock: bars until then, then the cues on
        // the start bar onward. Not before OPBridge has answered the press: until then the clock may be the one a song
        // is about to replace.
        if (transportAwaitingStart(target)) continue;
        const beat = transportBeat(target);
        if (beat === null) continue;
        const named = transportStartBeat(target);
        const intoBar = ((beat % 4) + 4) % 4;
        const untilStart = named !== null ? Math.max(0, named - beat) / 4 : intoBar < 1e-6 ? 0 : (4 - intoBar) / 4;
        for (const c of cues) {
          const d = untilStart + (c.position - run.parkedBar);
          if (c.position < run.parkedBar - 1e-6 || d <= 0 || d > 1) continue;
          const p = Math.round((1 - d) * 100) / 100;
          if (next[c.itemId] === undefined || p > next[c.itemId]!) next[c.itemId] = p;
        }
        continue;
      }
      const at = transportBar(target, t);
      if (!at || at.ended) continue;
      for (const [itemId, d] of barsUntilFires(t, cues, at.pos, 0, timelineLoops(t))) {
        const p = Math.round((1 - d) * 100) / 100;
        if (next[itemId] === undefined || p > next[itemId]!) next[itemId] = p;
      }
    }
    const prev = progress();
    const keys = Object.keys(next);
    if (keys.length !== Object.keys(prev).length || keys.some((k) => prev[k] !== next[k])) setProgress(next);
    if (anyRunning()) raf = requestAnimationFrame(frame);
  }
  const ensureLoop = (): void => { if (anyRunning() && raf === 0) raf = requestAnimationFrame(frame); };

  createRoot(() => {
    // Follow the primary and every paired peer, by id: the transport keeps one copy per bridge, and a new primary is
    // then a change here rather than '' quietly meaning another bridge.
    const targetIds = createMemo(() => {
      const ids = new Set<string>([primaryBridge()?.id ?? '']);
      for (const b of bridges()) if (b.bearer) ids.add(b.id);
      return [...ids].sort().join(' ');
    });
    createEffect(on(targetIds, (ids) => {
      const want = new Set(ids.split(' '));
      for (const [id, release] of followed) if (!want.has(id)) { release(); followed.delete(id); }
      for (const id of want) if (!followed.has(id)) followed.set(id, followTransport(id));
    }));
    // A run starting or ending anywhere starts or settles the frame loop.
    createEffect(() => {
      for (const id of targetIds().split(' ')) transportRun(id);
      untrack(() => {
        ensureLoop();
        if (!anyRunning() && Object.keys(progress()).length > 0) setProgress({});
      });
    });
  });
}
